import { Component, OnDestroy, OnInit } from '@angular/core';
import { CharterDAO } from '../../../../core/services/dao/charterDAO';
import {
  getUtcCharterDate,
  getUtcTermCharterDate,
} from '../../../../core/models/charter';
import { Charter } from 'anchor-shared/lib/model/entity/charter';

import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { ConstantMan } from '../../../../core/services/constantMan';
import {
  sortByDate,
  sortByStatus,
  sortCaseInsensitive,
} from '../../../../core/pure-functions/grids/grid-sorting-helper';
import { WaterBodyDAO } from '../../../../core/services/dao/waterBodyDAO';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { getTypeofCharter } from 'anchor-shared/lib/service/utils/charter-helper';

@Component({
  selector: 'app-charters-grid',
  templateUrl: './charters-grid.component.html',
  styleUrls: ['./charters-grid.component.scss'],
})
export class ChartersGridComponent implements OnInit, OnDestroy {
  charterStatus = 'lead';
  leadCharters: Charter[] = [];
  openCharters: Charter[] = [];
  cancelledCharters: Charter[] = [];
  completedCharters: Charter[] = [];
  termCharters: Charter[] = [];
  columnDefs: any;
  leadColumnDefs: any;
  openColumnDefs: any;
  completedColumnDefs: any;
  termColumnDefs: any;
  openRowData: any;
  leadRowData: any;
  cancelledRowData: any;
  completedRowData: any;
  termRowData: any;
  editPath = '/manage/manage-charter';
  selectedDateRange = 30;
  isMobile: boolean;
  subs = [];

  constructor(
    private charterDao: CharterDAO,
    private waterBodyDao: WaterBodyDAO,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.activeRoute.data.subscribe((data) => {
      if (activeRoute.snapshot.params.status) {
        this.charterStatus = activeRoute.snapshot.params.status;
      }
      this.selectedDateRange = 30;
      this.setGridData(this.charterStatus, data.charters.charterResolve);
    });

    const breakpointSub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    this.subs.push(breakpointSub);
  }

  ngOnInit() {}

  createTermColumnDefs() {
    return [
      {
        headerName: 'Charter ID',
        field: 'id',
        filter: 'text',
      },
      {
        headerName: 'Charter Date',
        field: 'date',
        filter: 'text',
        sort: 'desc',
        comparator: sortByDate,
      },
      {
        headerName: 'Customer',
        field: 'customerName',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Customer Phone Number',
        field: 'customerPhoneNumber',
      },
      {
        headerName: 'Boat',
        field: 'boat',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Total Price',
        field: 'price',
        filter: 'text',
      },
    ];
  }

  createColumnDefs() {
    return [
      {
        headerName: 'Charter Date',
        field: 'date',
        filter: 'text',
        sort: 'desc',
        comparator: sortByDate,
      },
      {
        headerName: 'Customer',
        field: 'customerName',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Customer Phone Number',
        field: 'customerPhoneNumber',
      },
      {
        headerName: 'Boat',
        field: 'boat',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Captain',
        field: 'captain',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Charter ID',
        field: 'id',
        filter: 'text',
      },
    ];
  }

  createLeadColumnDefs() {
    return [
      {
        headerName: 'Customer',
        field: 'customerName',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Phone Number',
        field: 'phoneNumber',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Charter Date',
        field: 'date',
        filter: 'date',
        sort: 'asc',
        comparator: sortByDate,
      },
      {
        headerName: 'Status',
        field: 'status',
        filter: 'text',
        comparator: sortByStatus,
      },
      {
        headerName: 'Created At',
        field: 'createdAt',
        filter: 'date',
        comparator: sortByDate,
      },
      {
        headerName: 'Boat',
        field: 'boat',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Water Body',
        field: 'waterBody',
        filter: 'text',
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        filter: 'text',
      },
    ];
  }

  createOpenColumnDefs() {
    return [
      {
        headerName: 'Customer',
        field: 'customerName',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Phone Number',
        field: 'phoneNumber',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Charter Date',
        field: 'date',
        filter: 'date',
        sort: 'asc',
        comparator: sortByDate,
      },
      {
        headerName: 'Time',
        field: 'time',
        filter: 'text',
      },
      {
        headerName: 'Status',
        field: 'status',
        filter: 'text',
        comparator: sortByStatus,
      },
      {
        headerName: 'Boat',
        field: 'boat',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Captain',
        field: 'captain',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Deposit Status',
        field: 'depositStatus',
        filter: 'text',
      },
      {
        headerName: 'Agreement',
        field: 'agreement',
        filter: 'text',
      },
      {
        headerName: 'Itinerary',
        field: 'itinerary',
        filter: 'text',
      },
      {
        headerName: 'Water Body',
        field: 'waterBody',
        filter: 'text',
      },
    ];
  }

  createCompletedColumnDefs() {
    return [
      {
        headerName: 'Customer',
        field: 'customerName',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Charter Date',
        field: 'date',
        filter: 'date',
        sort: 'desc',
        comparator: sortByDate,
      },
      {
        headerName: 'Boat',
        field: 'boat',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Captain Name',
        field: 'captain',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Charter ID',
        field: 'id',
        filter: 'text',
        comparator: sortCaseInsensitive,
      },
      {
        headerName: 'Reconciliation',
        field: 'reconciliationStatus',
        filter: 'text',
      },
      {
        headerName: 'Customer Phone Number',
        field: 'customerPhoneNumber',
      },
    ];
  }

  getChartersForDate() {
    this.charterDao
      .loadByStatus(this.charterStatus, this.selectedDateRange)
      .subscribe((res) => {
        this.setGridData(this.charterStatus, res);
      });
  }

  setGridData(status: string, charters?: Charter[]): void {
    if (status === ConstantMan.CHARTER_STATUSES.LEAD) {
      this.setupLeadCharters(charters);
    } else if (status === ConstantMan.CHARTER_STATUSES.OPEN) {
      this.setupOpenCharters(charters);
    } else if (status === ConstantMan.CHARTER_STATUSES.COMPLETED) {
      this.setupCompletedCharters(charters);
    } else if (status === ConstantMan.CHARTER_STATUSES.CANCELLED) {
      this.setupCancelledCharters(charters);
    } else if (status.toUpperCase() === 'TERM') {
      this.setupTermCharters(charters);
    }
  }

  setupTermCharters(charters?: Charter[]) {
    this.termCharters = charters;
    this.termRowData = this.formatTermRowDate(charters);
    this.termColumnDefs = this.createTermColumnDefs();
  }

  setupLeadCharters(charters?: Charter[]) {
    this.leadCharters = charters;
    this.leadColumnDefs = this.createLeadColumnDefs();
    this.leadRowData = this.formatLeadRowData(charters);
  }

  setupOpenCharters(charters?: Charter[]) {
    this.openCharters = charters;
    this.openRowData = this.formatOpenRowData(charters);
    this.openColumnDefs = this.createOpenColumnDefs();
  }

  setupCompletedCharters(charters?: Charter[]) {
    this.completedCharters = charters;
    this.completedRowData = this.formatCompletedRowData(charters);
    this.completedColumnDefs = this.createCompletedColumnDefs();
  }

  setupCancelledCharters(charters?: Charter[]) {
    this.cancelledCharters = charters;
    this.cancelledRowData = this.formatCancelledRowData(charters);
    this.columnDefs = this.createColumnDefs();
  }

  parseJson(json: string): any {
    // todo remove and replace this.numFormatter
    // let isString = true;
    let el = json as any;
    let isString = typeof el === 'string';
    while (isString) {
      el = JSON.parse(el);
      if (typeof el !== 'string') {
        isString = false;
      }
    }
    return el;
  }

  formatLeadRowData(charters) {
    const _charters = [];
    charters.forEach((charter) => {
      _charters.push({
        id: charter.id,
        customerName:
          charter.customerFirstName + ' ' + charter.customerLastName,
        phoneNumber: charter.customerPhoneNumber,
        date: getUtcCharterDate(charter).format('MM-DD-YY'),
        status: getTypeofCharter(charter),
        createdAt: moment(charter.createdAt).format('MM-DD-YY'),
        availabilityStatus: charter.availabilityStatus,
        boat: charter.boatName,
        waterBody: charter.waterBody
          ? charter.waterBody.displayName
          : 'NOT ASSIGNED',
        createdBy: charter.createdBy
          ? charter.createdBy.firstName + ' ' + charter.createdBy.lastName
          : '',
      });
    });
    return _charters;
  }

  formatCancelledRowData(charters) {
    const _charters = [];
    charters.forEach((charter) => {
      _charters.push({
        id: charter.id,
        customerName:
          charter.customerFirstName + ' ' + charter.customerLastName,
        customerPhoneNumber: charter.customerPhoneNumber,
        captain: charter.captainFirstName + ' ' + charter.captainLastName,
        date: getUtcCharterDate(charter).format('MM-DD-YY'),
        createdAt: moment(charter.createdAt).format('MM-DD-YY'),
        availabilityStatus: charter.availabilityStatus,
        boat: charter.boatName,
        depositStatus: charter.depositStatus,
        createdBy: charter.createdBy
          ? charter.createdBy.firstName + ' ' + charter.createdBy.lastName
          : '',
      });
    });
    return _charters;
  }

  formatTermRowDate(charters: Charter[]) {
    const _charters = [];
    charters.forEach((charter) => {
      let charterDates = getUtcTermCharterDate(charter);

      _charters.push({
        id: charter.id,
        customerName: charter.user.firstName + ' ' + charter.user.lastName,
        customerPhoneNumber: charter.user.phoneNumber,
        date: `${charterDates.startMoment.format(
          'MM-DD-YY',
        )} - ${charterDates.endMoment.format('MM-DD-YY')}`,
        boat: charter.boatName,
        price: charter.pricingMetaData.termCharterPricing.termPricePerDay,
      });
    });
    return _charters;
  }

  formatOpenRowData(charters: Charter[]) {
    const _charters = [];
    charters.forEach((charter) => {
      let itinerarySent;
      // @ts-ignore
      charter.itinerary = this.parseJson(charter.itinerary);
      if (charter.itinerary) {
        charter.itinerary.sent
          ? (itinerarySent = true)
          : (itinerarySent = false);
      }
      const depositPayment = charter.payments.find((x) => x.type === 'deposit');
      let depositStatusDisplay;
      if (!depositPayment) {
        depositStatusDisplay = 'Pending Collection';
      } else if (
        depositPayment?.paid ||
        (!depositPayment?.failed && !depositPayment?.paid)
      ) {
        depositStatusDisplay = 'Successful';
      } else if (depositPayment?.failed) {
        depositStatusDisplay = 'Failed';
      }

      _charters.push({
        id: charter.id,
        depositStatus: depositStatusDisplay,
        customerName:
          charter.customerFirstName + ' ' + charter.customerLastName,
        phoneNumber: charter.customerPhoneNumber,
        date: getUtcCharterDate(charter).format('MM-DD-YY'),
        time: this.buildTimeFrameFromCharter(charter),
        status: getTypeofCharter(charter),
        createdAt: moment(charter.createdAt).format('MM-DD-YY'),
        captain: charter.captainFirstName + ' ' + charter.captainLastName,
        boat: charter.boatName,
        agreement: charter.signedAgreementUrl ? 'SIGNED' : 'NOT SIGNED',
        itinerary: itinerarySent ? 'SENT' : 'NOT SENT',
        createdBy: charter.createdBy
          ? // @ts-ignore
            charter.createdBy.firstName + ' ' + charter.createdBy.lastName
          : '',
        waterBody: charter.waterBody
          ? charter.waterBody.displayName
          : 'NOT ASSIGNED',
      });
    });
    return _charters;
  }

  formatCompletedRowData(charters) {
    const _charters = [];
    charters.forEach((charter) => {
      _charters.push({
        id: charter.id,
        captain: `${charter.captainFirstName} ${charter.captainLastName}`,
        reconciliationStatus: charter.reconciliationStatus,
        customerPhoneNumber: charter.customerPhoneNumber,
        customerName:
          charter.customerFirstName + ' ' + charter.customerLastName,
        date: getUtcCharterDate(charter).format('MM-DD-YY'),
        createdAt: moment(charter.createdAt).format('MM-DD-YY'),
        owner: charter.boatOwnerFirstName + ' ' + charter.boatOwnerLastName,
        boat: charter.boatName,
        createdBy: charter.createdBy
          ? charter.createdBy.firstName + ' ' + charter.createdBy.lastName
          : '',
      });
    });
    return _charters;
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  private buildTimeFrameFromCharter(charter: Charter): string {
    const startTime =
      charter.startTime.split('-')[0] + charter.startTime.split(' ')[1];
    const startMoment = moment(startTime, 'h:mm a');
    return (
      startMoment.format('h:mma') +
      '-' +
      startMoment.add(charter.duration, 'hours').format('h:mma')
    );
  }
}
